import { useQuery } from '@tanstack/react-query';

import { reportingLeLensDatasetValuesPath } from '~/consts/reportingLeLensApi';
import { reportingLeLensCatalogueValuesPath } from '~/consts/reportingLeLensApi';

import type { LensFilterValues, LensPeriodDefault } from '~/types/leLens';

import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

export type LensColumnValues = {
  values: Array<string>;
  /** The dataset holds more distinct values than the list shows. */
  truncated: boolean;
  /** No list could be produced; the reader can still type values. */
  unavailable?: string;
  /** Plain-English measure used to put commercially important values first. */
  rankedBy?: string;
};

export const lensColumnValuesQueryKey = (
  lensId: string,
  datasetId: string,
  column: string,
  filters: LensFilterValues,
) => ['reporting', 'le-lens', lensId, 'column-values', datasetId, column, filters] as const;

/**
 * The values a dimension takes inside the current reporting period, so a chart filter is picked
 * from what the data holds rather than typed blind. Cached for the session: the list changes with
 * the dataset and the period, not with every keystroke.
 */
export function useGetLensColumnValues({
  lensId,
  datasetId,
  column,
  filters,
}: {
  lensId?: string;
  datasetId?: string;
  column?: string;
  filters: LensFilterValues;
}) {
  const enabled = Boolean(lensId && datasetId && column);
  return useQuery({
    queryKey: lensColumnValuesQueryKey(lensId ?? '', datasetId ?? '', column ?? '', filters),
    queryFn: ({ signal }) =>
      requestWithTimeout<LensColumnValues>(
        resolveReportingLeLensUrl(reportingLeLensDatasetValuesPath(lensId!, datasetId!)),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ column, filters }),
          signal,
        },
        'Listing the values took too long. You can type them instead.',
        45_000,
      ),
    enabled,
    retry: false,
    staleTime: 30 * 60 * 1000,
  });
}

export const catalogueColumnValuesQueryKey = (
  catalogueId: string,
  column: string,
  periodDefault?: LensPeriodDefault,
) => ['reporting', 'le-lens', 'catalogue', catalogueId, 'column-values', column, periodDefault] as const;

/**
 * Values for a planner-selected governed field before a Lens exists. This powers the final
 * plain-English scope decision without asking the reader to find or understand a dataset.
 */
export function useGetCatalogueColumnValues({
  catalogueId,
  column,
  periodDefault,
}: {
  catalogueId?: string;
  column?: string;
  periodDefault?: LensPeriodDefault;
}) {
  const enabled = Boolean(catalogueId && column);
  return useQuery({
    queryKey: catalogueColumnValuesQueryKey(catalogueId ?? '', column ?? '', periodDefault),
    queryFn: ({ signal }) =>
      requestWithTimeout<LensColumnValues>(
        resolveReportingLeLensUrl(reportingLeLensCatalogueValuesPath(catalogueId!)),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ column, ...(periodDefault ? { periodDefault } : {}) }),
          signal,
        },
        'Listing regions took too long. Try again.',
        45_000,
      ),
    enabled,
    retry: false,
    staleTime: 30 * 60 * 1000,
  });
}
